import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Title } from '../components/ui/Title';
import { Grid } from '../components/ui/Grid';
import { LoadMore } from '../components/ui/LoadMore';
import { StoryModal } from '../components/StoryModal';
import { StoryPanel } from '../components/StoryPanel';
import { FilterPanel } from '../components/filters/FilterPanel';
import { SEOHead } from '../components/SEOHead';
import { fetchStories, fetchBands, type StoryWithDetails } from '../services/api/stories';
import type { Band } from '../types/database';

const ITEMS_PER_PAGE = 12;

function Stories() {
  const { t } = useTranslation();
  const [stories, setStories] = useState<StoryWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selectedStory, setSelectedStory] = useState<StoryWithDetails | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBand, setSelectedBand] = useState<string | null>(null);
  const [bands, setBands] = useState<Band[]>([]);

  const handleStorySelect = (story: StoryWithDetails) => {
    setSelectedStory(story);
  };

  // Fetch available bands
  useEffect(() => {
    const loadBands = async () => {
      try {
        const bandsData = await fetchBands();
        setBands(bandsData);
      } catch (error) {
        console.error('Error fetching bands:', error);
      }
    };

    loadBands();
  }, []);

  const loadStories = async (start = 0, replace = true) => {
    try {
      setLoadingMore(!replace);
      
      const { stories: newStories, totalCount } = await fetchStories(
        start / ITEMS_PER_PAGE,
        ITEMS_PER_PAGE,
        searchQuery,
        selectedBand
      );

      setStories(prev => replace ? newStories : [...prev, ...newStories]);
      setHasMore(totalCount ? start + ITEMS_PER_PAGE < totalCount : false);
    } catch (error) {
      console.error('Error loading stories:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setPage(0);
    loadStories(0);
  }, [searchQuery, selectedBand]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadStories(nextPage * ITEMS_PER_PAGE, false);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedBand(null);
  };

  // Generate structured data for stories
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Band Stories auf jamnation",
    "description": "Entdecke die neuesten Stories, Updates und Nachrichten von Schweizer Bands",
    "url": "https://jamnation.ch/stories",
    "publisher": {
      "@type": "Organization",
      "name": "jamnation",
      "url": "https://jamnation.ch"
    },
    "blogPost": stories.slice(0, 10).map(story => ({
      "@type": "BlogPosting",
      "headline": story.title,
      "description": story.content.substring(0, 160),
      "datePublished": story.created_at,
      "dateModified": story.updated_at,
      "author": {
        "@type": "Person",
        "name": story.author.artist_name || story.author.full_name
      },
      "publisher": {
        "@type": "Organization",
        "name": story.band.name,
        "url": `https://jamnation.ch/bands/${story.band.slug}`
      },
      "url": `https://jamnation.ch/stories/${story.slug}`,
      "image": story.image_url
    }))
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8 md:py-24">
        <Title subtitle={t('stories.subtitle')}>
          {t('stories.title')}
        </Title>
        <div className="text-light/60">{t('stories.loading')}</div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Bands aus Deiner Nähe hautnah erleben"
        description="Entdecke die neuesten Stories, Updates und Behind-the-Scenes Inhalte von Schweizer Bands. Bleibe auf dem Laufenden mit deinen Lieblingsbands auf jamnation."
        keywords={[
          'Band Stories',
          'Musik Updates',
          'Schweizer Bands',
          'Band News',
          'Musik Stories',
          'Behind the Scenes',
          'Band Updates',
          'Musik Nachrichten'
        ]}
        url="https://jamnation.ch/stories"
        structuredData={structuredData}
      />

      <div className="container mx-auto px-6 py-8 md:py-24">
        <Title 
          subtitle={t('stories.subtitle')}
        >
          {t('stories.title')}
        </Title>

        <FilterPanel
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          selectedBand={selectedBand}
          onSelectBand={setSelectedBand}
          bands={bands}
          onClearFilters={handleClearFilters}
        />

        <Grid cols={4}>
          {stories.map((story) => (
            <StoryPanel key={story.id} onClick={handleStorySelect} story={story} />
          ))}
        </Grid>

        {stories.length === 0 && (
          <div className="text-center text-light/60 py-12">
            {t('stories.noStoriesFound')}
          </div>
        )}

        <LoadMore
          onClick={handleLoadMore}
          loading={loadingMore}
          hasMore={hasMore}
        />

        {selectedStory && (
          <StoryModal
            story={selectedStory}
            isOpen={true}
            onClose={() => setSelectedStory(null)}
          />
        )}
      </div>
    </>
  );
}

export default Stories;